import { fetchEntity } from '@classpass/saga-tools';
import omit from 'lodash-es/omit';
import { call, all, put, select, spawn } from 'redux-saga/effects';
import { getForApi } from 'helpers/utmValues';
import { Validator as paymentValidator } from 'components/PaymentMethods/Validator';
import { planById, msaOfferById, createSubscriptionCache } from 'redux/saga/entities/bound';
import { ec } from 'helpers/multitrack';
import { deepExtract } from 'helpers/deep';
import { getFormValues } from 'helpers/forms';
import { trackOptimizelyEvent } from 'helpers/optimizely';
import { segment } from 'helpers/segment';
import { GoogleAnalytics } from 'components/GoogleAnalytics/GoogleAnalytics';
import { trackGoogleTagManagerEvent } from 'components/GoogleTagManager/GoogleTagManager';
import { selectActivePaymentMethod } from 'components/PaymentMethodSelector/PaymentMethodSelector.reducer';
import { userId as selectUserId } from 'redux/saga/selectors';
import { selectCart } from 'redux/modules/cart';
import { getTokenPromise } from 'components/PaymentMethods/withStripeElements';
import { ApiError } from 'components/Errors/ApiError';
import { getUserLocation, gtmSafeRedirect } from 'redux/saga/helpers';
import { msaIdToAlias } from 'constants/msas';
import { baseSignupValidator } from './BaseSignupValidator';
import { setActiveProduct, selectActiveProduct } from '../Products.reducer';
import { paymentMethodRequiresRedirect } from './BaseProduct.helpers';

export const getPurchaseFormValues = getFormValues('purchase');

// This object maps between field on the purchase result and GTM data layer variables
const GTM_RESULT_MAPPING = {
  purchaseChargeAmount: 'charge_amount',
  currency: 'currency_code',
  giftRedemption: 'gift_redemption',
  msaId: 'msa_id',
  msaOfferId: 'msa_offer_id',
  planCode: 'plan_code',
  purchasePlanId: 'plan_id',
  planTierName: 'plan_tier_name',
  purchasePlanPrice: 'plan_price',
  taxAmount: 'tax_amount',
  purchaseTransactionId: 'id',
  offerName: 'offer_name',
  offerId: 'offer_id',
  promotionAlias: 'promotion_alias',
  purchasePromotionId: 'promotion_id',
  transactionId: 'id',
  transactionTotal: 'charge_amount',
  price: 'charge_amount',
  name: 'plan_code',
  sku: 'plan_id',
};

export class BaseProduct {
  static validator = paymentValidator;

  static signupValidator = baseSignupValidator;

  static errorMessager = err => ApiError.messageForDisplay([err], ApiError.billingErrorReducer);

  static *activate(productData) {
    if (!this || !this.key) {
      throw new Error('Activate can only be called from the context of a product with a key.');
    }

    yield put(setActiveProduct(this.key, productData));
  }

  static *getProductData() {
    return (yield select(selectActiveProduct)).data;
  }

  static *postPurchase(result) {
    yield call(BaseProduct.trackPurchaseSuccess, result);
    yield call(gtmSafeRedirect, '/welcome/refer');
  }

  static *submitSubscriptionPurchaseCache(client, userId, payload, { stripePromise }) {
    const { cache_key: cacheKey } = yield call(fetchEntity, createSubscriptionCache, [client, userId, payload]);
    const paymentSource = yield call(stripePromise, { cacheKey, userId });
    if (paymentSource) {
      const { redirectUrl } = paymentSource;

      yield call(BaseProduct.trackCachedSubscriptionPurchaseCompleted);
      window.location.assign(redirectUrl);
    }
  }

  static *submitPurchaseData(client, currentUserId, { stripeTokenPromiseKey, ...formData }, consents) {
    const [stripePromiseToCall, activeLocation, activePaymentMethod] = yield all([
      call(getTokenPromise, stripeTokenPromiseKey),
      call(getUserLocation, client),
      select(selectActivePaymentMethod),
    ]);
    const conversion = yield call(getForApi);

    const payload = {
      billing_info: {
        address: {
          country: formData.countryCode,
          postal_code: formData.ccPostalCode && String(formData.ccPostalCode),
        },
      },
      consents,
      conversion,
      ...(activeLocation && { gls_location_id: deepExtract('location.id', activeLocation) }),
      ...(formData.friendbuyCode && { friendbuy_referral_code: formData.friendbuyCode }),
      ...(formData.giftCode && { redemption_code: formData.giftCode }),
      ...(formData.promoCode && { promotion_code: formData.promoCode }),
      ...(formData.msaOfferId
        ? {
            msa_offer_id: parseInt(formData.msaOfferId, 10),
          }
        : {
            plan_id: parseInt(formData.planId, 10),
            msa_id: parseInt(formData.msaId, 10),
          }),
      ...(formData.corporateDetails &&
        formData.corporateDetails.partner && {
          corporate: {
            [formData.corporateDetails.partner]: {
              user_token: formData.corporateDetails.token,
              initial_url: formData.corporateDetails.initial_url,
            },
          },
        }),
    };

    try {
      if (paymentMethodRequiresRedirect(activePaymentMethod)) {
        return yield call(BaseProduct.submitSubscriptionPurchaseCache, client, currentUserId, payload, {
          stripePromise: stripePromiseToCall,
        });
      }

      const paymentToken = yield call(stripePromiseToCall);

      const payloadWithPaymentToken = {
        ...payload,
        ...(paymentToken && paymentToken.source
          ? { stripe_source: paymentToken.source }
          : { payment_token: paymentToken }),
      };

      return yield call(client.subscription.create, currentUserId, payloadWithPaymentToken);
    } catch (err) {
      const { code: errorCode = 'Unknown error code', message: errorMessage = 'Unknown error message' } = err || {};

      yield call(segment.track, 'Membership Purchase Errors', {
        category: 'Membership Purchase',
        label: `${activePaymentMethod},${errorCode}-${errorMessage}`,
        nonInteraction: true,
      });

      yield call(trackGoogleTagManagerEvent, {
        event: 'gaEvent',
        eventCategory: 'Membership Purchase',
        eventAction: 'Membership Purchase Errors',
        eventLabel: `${activePaymentMethod},${errorCode}-${errorMessage}`,
        eventNI: true,
      });
      throw err;
    }
  }

  static *trackCachedSubscriptionPurchaseCompleted() {
    const { msaId, planId, msaOfferId, giftCode } = yield select(getPurchaseFormValues);

    const [plan, msaOffer] = yield all([select(planById.data, planId), select(msaOfferById.data, msaOfferId)]);

    const userId = yield select(selectUserId);
    const msaAlias = msaIdToAlias(msaId);

    yield call(segment.track, 'successful_purchase', {
      userId,
      name: deepExtract('plan_code', plan),
      id: planId,
      quantity: 1,
      msaId,
      msaName: msaAlias,
      currency: plan && plan.currency_code && plan.currency_code.toUpperCase(),
      giftRedemption: !!giftCode,
      msaOfferId,
      planCode: deepExtract('plan_code', plan),
      purchasePlanId: planId,
      purchasePlanPrice: deepExtract('price', plan),
      offerName: deepExtract('offer.name', msaOffer),
      offerId: deepExtract('offer.id', msaOffer),
      promotionAlias: deepExtract('promotion_alias', msaOffer),
      purchasePromotionId: deepExtract('promotion_id', msaOffer),
      transactionId: Math.floor(Math.random() * 10000000), // random number, only used by GTM/GA ecommerce
      transactionTotal: deepExtract('price', plan),
      commitment: deepExtract('commitment', plan),
    });

    yield call(trackGoogleTagManagerEvent, {
      event: 'successful_purchase',
      userId,
      name: deepExtract('plan_code', plan),
      id: planId,
      quantity: 1,
      msaId,
      msaName: msaAlias,
      currency: plan && plan.currency_code && plan.currency_code.toUpperCase(),
      giftRedemption: !!giftCode,
      msaOfferId,
      planCode: deepExtract('plan_code', plan),
      purchasePlanId: planId,
      purchasePlanPrice: deepExtract('price', plan),
      offerName: deepExtract('offer.name', msaOffer),
      offerId: deepExtract('offer.id', msaOffer),
      promotionAlias: deepExtract('promotion_alias', msaOffer),
      purchasePromotionId: deepExtract('promotion_id', msaOffer),
      transactionId: Math.floor(Math.random() * 10000000), // random number, only used by GTM/GA ecommerce
      transactionTotal: deepExtract('price', plan),
      commitment: deepExtract('commitment', plan),
    });
  }

  /* eslint-disable no-unused-vars, no-empty-function */
  static *trackPurchaseFailure() {}
  /* eslint-enable no-unused-vars, no-empty-function */

  static *trackPurchaseLoad() {
    const { msaId, planId, msaOfferId } = yield select(getPurchaseFormValues);

    const [userId, cart, plan, msaOffer] = yield all([
      select(selectUserId),
      select(selectCart),
      select(planById.data, planId),
      select(msaOfferById.data, msaOfferId),
    ]);

    yield all([
      spawn(ec, 'Purchase - Start', {
        plan_id: planId,
        plan_code: plan && plan.plan_code,
        promotion_alias: msaOffer && msaOffer.promotion_alias,
      }),
      spawn(GoogleAnalytics.sendPageView, '/signup/v2/membership/start'),
      spawn(trackGoogleTagManagerEvent, {
        event: 'view_cart',
        msaId,
        userId,
        planId,
        planName: plan && plan.name,
        offerName: msaOffer && msaOffer.offer_name,
        msaOfferId,
        price: cart.subTotal,
      }),
    ]);

    yield all([
      call(segment.track, 'Purchase - Start', {
        plan_id: planId,
        plan_code: plan && plan.plan_code,
        promotion_alias: msaOffer && msaOffer.promotion_alias,
      }),
      call(segment.page, '/signup/v2/membership/start'),
      call(segment.track, 'view_cart', {
        msaId,
        userId,
        planId,
        planName: plan && plan.name,
        offerName: msaOffer && msaOffer.offer_name,
        msaOfferId,
        price: cart.subTotal,
      }),
    ]);
  }

  static *trackPurchaseSuccess(result) {
    const { planId, msaOfferId } = yield select(getPurchaseFormValues);

    const [plan] = yield all([select(planById.data, planId), select(msaOfferById.data, msaOfferId)]);

    const gtmDataObject = Object.keys(GTM_RESULT_MAPPING)
      .filter(gtmKey => result[GTM_RESULT_MAPPING[gtmKey]])
      .reduce(
        (data, gtmKey) =>
          Object.assign(data, {
            [gtmKey]:
              gtmKey === 'currency'
                ? result[GTM_RESULT_MAPPING[gtmKey]].toUpperCase()
                : result[GTM_RESULT_MAPPING[gtmKey]],
          }),
        { event: 'successful_purchase' }
      );

    const userId = yield select(selectUserId);
    const msaAlias = msaIdToAlias(result.msa_id);

    yield all([
      call(segment.track, 'successful_purchase', {
        ...omit(gtmDataObject, 'event'),
      }),
      call(trackGoogleTagManagerEvent, {
        ...gtmDataObject,
        commitment: deepExtract('commitment', plan),
        userId,
        msaName: msaAlias,
      }),
      call(trackOptimizelyEvent, 'successful_subscription_purchase'),
      call(trackOptimizelyEvent, 'successful_overall_purchase'),
    ]);
  }

  /* eslint-disable no-unused-vars, no-empty-function */
  static *trackSignupFailure() {}

  static *trackSignupLoad() {}

  static *trackSignupSuccess() {}
  /* eslint-enable no-unused-vars, no-empty-function */
}
